// Status reads the integrations page already uses.
// IntegrationsCard and the /v2/connections overview both call these
// URLs. Connect, disconnect, and OAuth stay in IntegrationsCard.

import { apiJson } from '../../auth/apiFetch';
import { isSecretText } from '../../marketingPlans/client';

export type DirectConnection = 'instagram' | 'meta-ads' | 'google-ads' | 'shopify';

export type ConnectionChip = {
  color: 'green' | 'yellow' | 'red' | 'gray';
  label: string;
};

export type ConnectionStatusLoad<T = unknown> =
  | { ok: true; body: T }
  | { ok: false; error: string };

export function connectionStatusUrl(kind: DirectConnection, brandId: string): string {
  switch (kind) {
    case 'instagram':
      return '/api/integrations/instagram/status';
    case 'meta-ads':
      return '/api/integrations/meta-ads/status';
    case 'google-ads':
      return '/api/integrations/google-ads/status';
    case 'shopify':
      return `/api/integrations/shopify/status?brandId=${encodeURIComponent(brandId)}`;
  }
}

// Drop bearer tokens, Meta EAA tokens, access_token, and act_ ids
// before an error string is rendered. Same rule as isSecretText.
export function statusErrorText(message: string, fallback = 'Could not load status'): string {
  if (!message || isSecretText(message)) return fallback;
  return message;
}

export async function fetchConnectionStatus<T = unknown>(
  kind: DirectConnection,
  brandId: string
): Promise<ConnectionStatusLoad<T>> {
  try {
    const body = await apiJson<T>(connectionStatusUrl(kind, brandId));
    return { ok: true, body };
  } catch (err: unknown) {
    const raw = err instanceof Error ? err.message : '';
    return { ok: false, error: statusErrorText(raw) };
  }
}

// Map a loaded status payload to a chip. "Connected" is returned only
// when the payload says the credential is active (or, for Shopify, the
// credential object is present). A missing credential is Not connected.
// A failed request is not passed here — callers surface that as Error.
export function directConnectionChip(kind: DirectConnection, body: unknown): ConnectionChip {
  const rec = asRec(body);
  const cred = asRec(rec?.credential);
  if (kind === 'shopify') {
    if (cred) return { color: 'green', label: 'Connected' };
    const request = typeof asRec(rec?.accessRequest)?.status === 'string'
      ? String(asRec(rec?.accessRequest)?.status)
      : '';
    if (request === 'pending' || request === 'ready') return { color: 'yellow', label: 'Pending' };
    return { color: 'gray', label: 'Not connected' };
  }
  if (!cred) return { color: 'gray', label: 'Not connected' };
  if (cred.status === 'pending') return { color: 'yellow', label: 'Pending' };
  if (cred.status === 'active') return { color: 'green', label: 'Connected' };
  return { color: 'gray', label: 'Not connected' };
}

function asRec(value: unknown): Record<string, unknown> | null {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}
